import Text from '@axa-japan/design-system-react/Text';
import { useCallback, useEffect, useRef, useState } from 'react';
import crossIconUrl from '../../assets/cross_icon.svg';
import useOrientation from '../../hooks/useOrientation';
import { useUserContext } from '../../providers/UserProvider';
import { CameraInterface, createCameraInterface, DEFAULT_VIDEO_CONSTRAINTS, parseCameraError } from '../../utils/cameraUtils';
import CustomModal from '../common/CustomModal';
import OrientationWarning from '../common/OrientationWarning/OrientationWarning';

type CameraProps = {
  onCapture: (file: File, src: string | null) => void;
  onClose: () => void;
};

const Camera = ({ onCapture, onClose }: CameraProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraRef = useRef<CameraInterface | null>(null);
  const deviceOrientation = useOrientation();
  const isLandscape = deviceOrientation === 'landscape';

  const { setLoader } = useUserContext();
  // Stabilize setLoader reference to prevent infinite loop in useEffect
  const setLoaderRef = useRef(setLoader);
  setLoaderRef.current = setLoader;

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [capturedImgFile, setCapturedImgFile] = useState<File>();
  const [capturedImgSrc, setCapturedImgSrc] = useState<string | null>();
  const [isCameraReady, setIsCameraReady] = useState(false);

  const closeCamera = useCallback(() => {
    if (cameraRef.current) {
      cameraRef.current.close();
      cameraRef.current = null;
    }
    setIsCameraReady(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeCamera = async () => {
      if (!videoRef.current || !isLandscape) return;

      try {
        setLoaderRef.current(true);
        const camera = createCameraInterface();
        cameraRef.current = camera;
        await camera.initialize(videoRef.current, DEFAULT_VIDEO_CONSTRAINTS);
        if (isMounted) {
          setIsCameraReady(true);
          setLoaderRef.current(false);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(parseCameraError(`${error}`));
          setLoaderRef.current(false);
          setIsErrorModalOpen(true);
        }
      }
    };

    if (isLandscape) {
      initializeCamera();
    }

    return () => {
      isMounted = false;
      closeCamera();
    };
  }, [isLandscape, closeCamera]);

  const capture = async () => {
    try {
      if (cameraRef.current) {
        const result = await cameraRef.current.takePhoto();
        if (result) {
          setCapturedImgSrc(result.dataUrl);
          setCapturedImgFile(result.file);
        }
      }
    } catch (e) {
      setIsErrorModalOpen(true);
      setErrorMessage(`${e}`);
    }
  };

  const handleClose = useCallback(() => {
    closeCamera();
    onClose();
  }, [closeCamera, onClose]);

  const handleRetake = useCallback(() => {
    setCapturedImgSrc(null);
    setCapturedImgFile(undefined);
  }, []);

  if (deviceOrientation === 'portrait') {
    return <OrientationWarning />;
  }

  return (
    <div className="m-auto w-full flex flex-col justify-between h-full bg-white">
      <CustomModal buttonLabel="OK" isWarningModal open={isErrorModalOpen} title="カメラを起動できません。" onClose={handleClose}>
        <div>{errorMessage}</div>
      </CustomModal>
      <button type="button" onClick={handleClose} className="fixed top-5 right-5 border-0 bg-transparent p-0 cursor-pointer">
        <img className="w-10" src={crossIconUrl} alt="Close" />
      </button>

      <div className="flex flex-row !w-full h-full justify-around items-center gap-4 pb-10 pt-20 px-6">
        <div className="h-full w-full bg-interactiveVisitedGrey flex items-center justify-center relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`transition delay-150 duration-300 ease-in-out ${isCameraReady ? 'opacity-100' : 'opacity-0'}`}
            style={capturedImgSrc ? { position: 'absolute', visibility: 'hidden' } : undefined}
          />

          {capturedImgSrc && <img src={capturedImgSrc} className="absolute object-contain z-10" alt="Captured" />}
        </div>
        {capturedImgSrc ? (
          <div className="h-full min-w-52 rounded flex flex-col gap-4 justify-center items-center">
            <button
              type="button"
              className="bg-interactiveActivePrimary h-1/2 w-full items-center flex justify-center border-0 p-0 cursor-pointer"
              onClick={() => onCapture(capturedImgFile as File, capturedImgSrc)}
            >
              <Text size="lg" weight="bold" color="primary-white">
                はい
              </Text>
            </button>

            <button
              type="button"
              className="bg-interactiveActivePrimary h-1/2 w-full flex justify-center items-center border-0 p-0 cursor-pointer"
              onClick={handleRetake}
            >
              <Text size="lg" weight="bold" color="primary-white">
                画像を撮り直す
              </Text>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="bg-interactiveActivePrimary h-full min-w-52 rounded flex justify-center items-center border-0 p-0 cursor-pointer"
            onClick={capture}
          >
            <Text size="lg" weight="bold" color="primary-white">
              端末カメラで撮影
            </Text>
          </button>
        )}
      </div>
    </div>
  );
};

export default Camera;
